// This app's runtime config shape. The loading mechanics (fetch, the config.local.json override, and
// required-field validation) live in the shared @optimyl/config package; only the shape and the list of
// fields this app cannot start without belong here.

import { loadConfig as load, getConfig as get, type CoreConfig } from "@optimyl/config";

export interface AppConfig extends CoreConfig {
  /** Salesforce org origin for record links, e.g. https://optimyl.lightning.force.com (no trailing slash). */
  SALESFORCE_BASE_URL: string;
  /**
   * Local development only, from config.local.json: the email sent as X-Actor-Id while AUTH_ENABLED is false, so a
   * locally running API knows who is calling. Ignored once auth is on; never set in a deployed config.json.
   */
  DEV_ACTOR_EMAIL?: string;
  /** The Identity Server client id, `uw-workbench-spa`. IDENTITY_URL is the identity server for the environment. Required once AUTH_ENABLED is true. */
  AUTH_CLIENT_ID?: string;
  /** The audience scope the API requires, `optimyl:uw-workbench`. Required once AUTH_ENABLED is true. */
  AUTH_API_SCOPE?: string;
}

let testOverride: AppConfig | null = null;

/**
 * API_BASE_URL is required because every screen reads through the API; SALESFORCE_BASE_URL because Opportunity and
 * Quote Owner links render on every group screen. IDENTITY_URL, AUTH_CLIENT_ID, and AUTH_API_SCOPE are checked where
 * sign-in is built (auth/oidcConfig.ts) so an environment with AUTH_ENABLED false can leave them blank.
 */
export const loadConfig = (): Promise<AppConfig> => load<AppConfig>({ require: ["API_BASE_URL", "SALESFORCE_BASE_URL"] });

export function getConfig(): AppConfig {
  return testOverride ?? get<AppConfig>();
}

/** Test-only helper so suites can install a config without fetching. */
export function setConfigForTests(config: AppConfig): void {
  testOverride = config;
}
